//!#system	

<template>
	<div v-if="isLoaded">
		<v-layout wrap>
			<v-flex xs4>
				<div style="margin-right: 20px;">
					<v-select
						v-model="model.name"
						:items="actions"
						label="Choose an action"
						:disabled="!bucket"
						item-value="name"
						item-text="label"
						@change="changeAction"
					>
						<template v-slot:item="data">
							<template v-if="data.item.disabled">
								<v-list-item-content v-text="data.item.label"></v-list-item-content>
								<v-icon small color="grey" title="Your current plan does not offer this action.">fa fa-lock</v-icon>
							</template>
							<template v-else>
								<v-list-item-content v-text="data.item.label"></v-list-item-content>
							</template>
						</template>
					</v-select>
				</div>
			</v-flex>
			<v-flex xs4>
				<div style="margin-right: 20px;">
					<div v-for="(param, name) in actionParams" :key="name">
						<div v-if="model.name === 'changeValue' && name === 'path'">
							<v-combobox 
								v-model="model.values['reference']" 
								:items="previouslySpecifiedMatchers" 
								label="Field or JSON path to change"
								item-value="name"
								item-text="label"
								@input="choosePath()"
								@change="choosePath()"
								:error="changeValuePathErrorState.isError"
								:error-messages="changeValuePathErrorState.messages"
							></v-combobox>
						</div>
						<div v-else-if="['lockIt', 'unlockIt'].includes(model.name) && name === 'lockType'">
							<v-select 
								v-model="model.values[name]" 
								:items="['Update lock', 'Action lock', 'Both']" 
								label="Lock type"
							></v-select>
						</div>
						<div v-else-if="['linkTo'].includes(model.name) && name === 'toUniqueValue'">
							<v-tabs
								v-model="linkToTabs"
							>
								<v-tabs-slider></v-tabs-slider>
								<v-tab>
									Internal link
								</v-tab>
								<v-tab>
									External link
								</v-tab>
							</v-tabs>
							<v-tabs-items v-model="linkToTabs">
								<v-tab-item
									:value="0"
									class="pt-6"
								>
									<v-autocomplete
										v-model="model.values.toBucket" 
										:items="buckets"
										label="Link to bucket"
									/>
									<v-text-field
										v-model="model.values.toUniqueValue"
										label="Unique ID to link to"
									/>
									<v-text-field
										label="Description"
										hint="Describe this link"
										v-model="model.values.description"
									/>
									<v-switch
										label="Link back?"
										v-model="model.values.linkBack"
										inset
										class="ml-2"
									></v-switch>
								</v-tab-item>
								<v-tab-item
									:value="1"
									class="pt-6"
								>
									<v-text-field
										label="Link to URL"
										hint="Enter a URL that starts with http..."
										:rules="[v => (!v || (v + '').startsWith('http')) || 'Make sure your URL starts with http']"
										v-model="model.values.url"
									/>
									<v-text-field
										label="Description"
										hint="Describe this link"
										v-model="model.values.description"
									/>
								</v-tab-item>
							</v-tabs-items>
						</div>
						<div v-else-if="['tagItWith', 'unTagItWith'].includes(model.name) && name === 'tag'">
							<v-select 
								v-model="model.values[name]" 
								:items="availableTags" 
								label="Tag"
								item-value="tag"
								item-text="tag"
							></v-select>
						</div>
						<div v-else-if="['addToQueue', 'removeFromQueue'].includes(model.name) && name === 'queueCode'">
							<v-select 
								v-model="model.values[name]" 
								:items="availableQueues" 
								label="Queue code"
								item-value="queueCode"
								item-text="queueCode"
							></v-select>
						</div>
						<div v-else-if="['sendEmail'].includes(model.name) && name === 'templateName'">
							<v-select 
								v-model="model.values[name]" 
								:items="emailTemplates" 
								label="Template"
								item-value="type"
								item-text="type"
							></v-select>
						</div>
						<div v-else-if="['sendEmail'].includes(model.name) && name === 'to'">
							<v-combobox 
								v-model="model.values[name]" 
								:items="userGroups" 
								label="User group or email address"
								item-value="name"
								item-text="name"
							></v-combobox>
						</div>
						<div v-else-if="['webhook'].includes(model.name) && name === 'serviceKey'">
							<v-select 
								v-model="model.values[name]" 
								:items="webServices" 
								label="Web Service"
								item-value="serviceKey"
								item-text="serviceKey"
							></v-select>
						</div>
						<div v-else-if="['webhook'].includes(model.name) && name === 'httpMethod'">
							<v-select 
								v-model="model.values[name]" 
								:items="['GET', 'POST', 'PUT', 'DELETE']"
								label="HTTP Method"
							></v-select>
						</div>
						<div v-else-if="['noPermission'].includes(model.name) && name === 'groups'">
							<v-select 
								v-model="model.values[name]"
								:items="userGroups"
								label="User groups"
								item-value="name"
								item-text="name"
								multiple
							></v-select>
						</div>
						<div v-else-if="Array.isArray(action.params)">
							<v-text-field
								v-for="param in action.params"
								:key="param.name"
								v-model="model.values[param.name]"
								:label="tools.camelToTitle(param.name)"
							/>
						</div>
						<div v-else>
							<v-text-field
								:label="param.description"
								v-model="model.values[name]"
								:value="model.values[name]"
								@input="updateModel"
							/>
						</div>
					</div>
				</div>
			</v-flex>
			<v-flex xs3>
				<div style="margin-right: 20px;">
					<v-select
						v-model="model.onError"
						:items="onErrorOptions"
						item-value="name"
						item-text="label"
						label="If an error happens..."
						@input="updateModel"
					/>
				</div>
			</v-flex>
			<v-flex xs1 v-if="canRemove">
				<v-tooltip bottom>
					<template v-slot:activator="{ on }">
						<v-btn class="removeActionButton" v-on="on" color="warning" x-small dark @click="removeThisAction(model.sig)">
							<v-icon dark x-small>mdi-minus</v-icon>
						</v-btn>
					</template>
					<span>Remove this action</span>
				</v-tooltip>
			</v-flex>
		</v-layout>
	</div>
	<div v-else class="pb-6">
		<v-progress-linear :indeterminate="true"></v-progress-linear>
	</div>
</template>
<script>

import apiService from '@/apiService';
import tools from '@/tools';
import series from 'to-series';

const baseKey = function(start, end) {
	return Math.floor(Math.random() * end) + start;
};

const defaultAction = function() {
	return {
		sig: 'action-' + baseKey(1500, 2800),
		name: 'addToQueue',
		label: 'Add it to a queue',
		onError: 'abort',
		values: {}
	}
};

const canChangeValueTriggers = ['create', 'update'];

export default {
	name: 'rulesAction',
	props: {
		routeInfo: {
			type: Object
		},
		value: {
			type: Object
		},
		bucket: {
			type: String
		},
		canRemove: {
			type: Boolean,
			default: false
		},
		matchers: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			isLoading: false,
			buckets: [],
			model: this.value.name ? this.value : defaultAction(),
			actions: [],
			onErrorOptions: [{ name: 'ignore', label: 'Ignore errors and just continue' }, { name: 'abort', label: 'Abort all processing and return an error' }],
			availableQueues: null,
			availableTags: null,
			userGroups: [],
			emailTemplates: [],
			webServices: [],
			tools,
			linkToTabs: 0
		}
	},
	computed: {
		isLoaded() {
			return !this.isLoading;
		},
		action() {
			if (!this.actions || this.actions.length === 0 || !Array.isArray(this.actions)) return {};
			if (!this.model.name) return {};
			let action = { params: {} };
			this.actions.forEach(act => {
				if (act.name === this.model.name) {
					action = act;
				}
			});
			return action;
		},
		actionParams() {
			return this.action.params;
		},
		previouslySpecifiedMatchers() {
			const displayMatchers = [];
			this.matchers.forEach(matcher => {
				displayMatchers.push({
					sig: matcher.sig,
					name: matcher.sig,
					label: '"' + matcher.path + ' ' + matcher.operator + ' ' + matcher.value + '"'
				})
			});
			return displayMatchers;
		},
		changeValuePathErrorState() {
			if (this.model.name === 'changeValue') {
				if (typeof this.model.values.reference === 'string') {
					// they typed it in... validate that it does not contain '[any]'
					if (this.model.values.reference.includes('[any]')) {
						return {
							isError: true,
							messages: ['You cannot type in "[any]" paths here.  Make sure they come from previously matched paths above, so that we know excactly what element you are referring to.']
						}
					}
				}
			}
			return {
				isError: false,
				messages: []
			}
		}
	},
	methods: {
		getBuckets(done) {
			this.$store.dispatch('app/buckets', { 
				options: {}, 
				callback: (err, buckets) => {
					this.buckets = buckets;
					return done(err);
				} 
			});
		},
		formatValues() {
			let params = this.actionParams;
			let action = this.action;
			if (action.label) this.model.label = action.label;
		},
		changeAction() {
			this.formatValues();
			let params = this.actionParams;
			this.model.values = {}; // reset it
			for(let key in params) {
				this.model.values[key] = null;
			}
			this.updateModel();
		},
		setAction() {
			this.formatValues();
			this.updateModel();
		},
		getActions(objectPath, done) {
			this.actions = [];
			let objectPathArray = objectPath.split('/');
			let namespace = objectPathArray[0];
			let objectNamePlural = objectPathArray[1];
			apiService.rulesActions({
				...this.routeInfo,
				pluralName: objectNamePlural,
				namespace: namespace
			}, (err, response, meta) => {
				this.actions = response;
				if (done) return done(err);
			});
		},
		getUserGroups(done) {
			apiService.list({ namespace: 'uac', pluralName: 'groups' }, (err, groups) => {
				if (err) return done(err);
				this.userGroups = groups;
				return done();
			});
		},
		getEmailTemplates(done) {
			apiService.list({ namespace: 'comms', pluralName: 'emailTemplates', filters: {
				systemGenerated: {
					operator: 'is',
					value: false
				}
			} }, (err, templates) => {
				if (err) return done(err);
				this.emailTemplates = templates;
				return done();
			});
		},
		getAvailableTags(done) {
			if (!this.bucket) return done();
			let [ namespace, pluralName ] = this.bucket.split('/');
			apiService.availableTags({
				namespace,
				pluralName
			}, (err, tags) => {
				if (err) return done(err);
				this.availableTags = tags;
				return done();
			});
		},
		getAvailableQueues(done) {
			if (!this.bucket) return done();
			let [ namespace, pluralName ] = this.bucket.split('/');
			apiService.availableQueues({
				namespace,
				pluralName
			}, (err, queues) => {
				if (err) return done(err);
				this.availableQueues = queues;
				return done();
			});
		},
		getWebServices(done) {
			apiService.list({
				namespace: 'bpa',
				pluralName: 'webServices'
			}, (err, webServices) => {
				if (err) return done(err);
				this.webServices = webServices;
				return done();
			});
		},
		removeThisAction(sig) {
			this.$emit('remove', sig);
		},
		getInitialData() {
			this.isLoading = true;
			series()
				.first(cb => {
					if (!this.bucket) return cb();
					this.getActions(this.bucket, cb);
				})
				.next(cb => {
					if (!this.bucket) return cb();
					this.getAvailableQueues(cb);
				})
				.next(cb => {
					if (!this.bucket) return cb();
					this.getAvailableTags(cb);
				})
				.next(cb => {
					this.getUserGroups(cb);
				})
				.next(cb => {
					this.getEmailTemplates(cb);
				})
				.next(cb => {
					this.getBuckets(cb);
				})
				.next(cb => {
					this.getWebServices(cb);
				})
				.end(err => {
					this.isLoading = false;
					this.setAction();
				});
		},
		choosePath() {
			let values = this.model.values;
			if ('reference' in values && typeof values.reference === 'object' && 'name' in values.reference && values.reference.name.startsWith('item-')) {
				// They are referencing a matcher from the above UI
				this.matchers.forEach(matcher => {
					if (matcher.sig === values.reference.name) {
						this.model.values.matcher = matcher;
						delete this.model.values.path;
					}
				});
			} else if ('reference' in values && typeof values.reference === 'string') {
				this.model.values.path = values.reference;
				delete this.model.values.matcher;
			}
			this.updateModel();
		},
		updateModel() {
			this.$emit('input', { ...this.value, ...this.model });
		}
	},
	watch: {
		bucket: function(value, oldValue) {
			this.getInitialData();
		},
		// No used anymore but can be useful in the future
		/*
		triggers: function(value, oldValue) {
			let hasTriggerThatCannotChangeValue = false,
				hasCreateTrigger = false;

			this.triggers.map(trigger => {
				if (!canChangeValueTriggers.includes(trigger)) {
					hasTriggerThatCannotChangeValue = true;
				}

				if (trigger === 'create') {
					hasCreateTrigger = true;
				}
			});

			this.actions.map(action => {
				action.disabled = false;

				// Only `create` and `update` can change a value
				if (hasTriggerThatCannotChangeValue && action.name == 'changeValue') {
					action.disabled = true;
				}

				// All the other actions require an ID so choosing `create` will disable all those actions
				if (hasCreateTrigger && action.name !== 'changeValue') {
					action.disabled = true;
				}
			});
		}
		*/
	},
	created() {
		this.getInitialData();
		if (this.model.values.url) this.linkToTabs = 1;
	}
};
</script>

<style scoped>
.removeActionButton {
	margin-left: 20px;
	margin-bottom: -50px;
}
</style>